import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { HiSearch, HiFilter, HiEye, HiPencil, HiCheckCircle, HiClock, HiExclamation, HiLocationMarker, HiTrash, HiPlus, HiRefresh, HiX, HiZoomIn } from 'react-icons/hi'
import { Badge, Spinner } from 'flowbite-react'
import { useAuth } from '../contexts/AuthContext'
import axios from 'axios'
import toast from 'react-hot-toast'
import IsolatedLocationMap from '../components/IsolatedLocationMap'
import ImageCarousel from '../components/ImageCarousel'
import ThumbnailFan from '../components/ThumbnailFan'
import PlusCode from '../components/PlusCode'
import { OpenLocationCode } from 'open-location-code'

// Calculate Plus Code from latitude and longitude
const calculatePlusCode = (lat, lng) => {
  try {
    if (!lat || !lng) return null
    
    const olc = new OpenLocationCode()
    const plusCode = olc.encode(lat, lng, 11) // 11 is extra precision
    return plusCode
  } catch (error) {
    console.error('Error calculating Plus Code:', error)
    return null
  }
}

// Extract direction data from attachments
const extractDirectionFromAttachments = (attachments) => {
  if (!attachments || !Array.isArray(attachments)) return null
  
  // Look for direction data in attachment metadata
  for (const attachment of attachments) {
    // Check if attachment has direction data
    if (attachment.direction !== undefined && attachment.direction !== null) {
      return attachment.direction
    }
    
    // Check if attachment has bearing data
    if (attachment.bearing !== undefined && attachment.bearing !== null) {
      return attachment.bearing
    }
    
    // Check if attachment has heading data
    if (attachment.heading !== undefined && attachment.heading !== null) {
      return attachment.heading
    }
    
    // Check if attachment has azimuth data
    if (attachment.azimuth !== undefined && attachment.azimuth !== null) {
      return attachment.azimuth
    }
  }
  
  return null // No direction data found
}

const IssueAdmin = () => {
  const { token } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [issues, setIssues] = useState([])
  const [filteredIssues, setFilteredIssues] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPriority, setFilterPriority] = useState('all')
  const [filterCategory, setFilterCategory] = useState('all')
  const [selectedIssue, setSelectedIssue] = useState(null)
  const [modalMode, setModalMode] = useState('preview') // 'preview' or 'edit'
  const [showAddIssue, setShowAddIssue] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [locationModalIssue, setLocationModalIssue] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [itemsPerPage] = useState(25)
  const [refreshing, setRefreshing] = useState(false)
  const [, setPaginationInfo] = useState(null)
  
  // Image carousel state
  const [showImageCarousel, setShowImageCarousel] = useState(false)
  const [carouselImages, setCarouselImages] = useState([])
  const [carouselTitle, setCarouselTitle] = useState('')
  const [carouselInitialIndex, setCarouselInitialIndex] = useState(0)
  const [addressUpdated, setAddressUpdated] = useState(false)
  const [, setOriginalLocation] = useState(null)
  
  // Multiple select delete state
  const [selectedIssueIds, setSelectedIssueIds] = useState([])
  const [isDeleting, setIsDeleting] = useState(false)


  // API Base URL
  const API_BASE = 'https://api.cityseecityfix.com/api/v1'

  // Compute full address from individual fields
  const computeFullAddress = (issue) => {
    if (!issue) return ''
    
    const parts = []
    
    // Combine house number and road with a space
    if (issue.house_number && issue.road) {
      parts.push(`${issue.house_number} ${issue.road}`)
    } else if (issue.house_number) {
      parts.push(issue.house_number)
    } else if (issue.road) {
      parts.push(issue.road)
    }
    
    // Add address2 if present
    if (issue.address2) parts.push(issue.address2)
    
    // Add remaining fields with commas
    if (issue.city) parts.push(issue.city)
    if (issue.state) parts.push(issue.state)
    if (issue.postcode) parts.push(issue.postcode)
    if (issue.county) parts.push(issue.county)
    if (issue.country_iso2) parts.push(issue.country_iso2)
    
    return parts.join(', ')
  }

  // New issue form state - updated to match API schema
  const [newIssue, setNewIssue] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    status: 'open',
    house_number: '',
    road: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    county: '',
    country_iso2: 'US',
    facility_name: '',
    facility_type: '',
    facility_subtype: ''
  })

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE}/categories/`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setCategories(response.data)
      // Set default category if available
      if (response.data.length > 0 && !newIssue.category) {
        setNewIssue(prev => ({ ...prev, category: response.data[0].name }))
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error)
      // Fallback to default categories if API fails
      setCategories([
        { name: 'Roads', id: 1 },
        { name: 'Lighting', id: 2 },
        { name: 'Sidewalks', id: 3 },
        { name: 'Vandalism', id: 4 },
        { name: 'Traffic', id: 5 },
        { name: 'Other', id: 6 }
      ])
    }
  }

  // Fetch issues from API with server-side pagination and filtering
  const fetchIssues = async () => {
    try {
      setLoading(true)
      
      // Pagination is the only part of this the API actually honours.
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      }

      // WARNING: GET /api/v1/issues accepts only skip, limit, union, authorities
      // and reporter_id (verified against the published OpenAPI spec). The
      // status/priority/category params below are silently ignored by FastAPI,
      // and nothing filters them client-side either — so those three dropdowns
      // currently have no effect on what is displayed. They are left in place
      // pending either backend support or client-side filtering.

      // Search is handled client-side, not sent to API

      // Add status filter if not 'all'
      if (filterStatus !== 'all') {
        params.status = filterStatus
      }
      
      // Add priority filter if not 'all'
      if (filterPriority !== 'all') {
        params.priority = filterPriority
      }
      
      // Add category filter if not 'all'
      if (filterCategory !== 'all') {
        params.category = filterCategory
      }
      
      const response = await axios.get(`${API_BASE}/issues`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      })
      
      
      // Handle the new API response format with pagination metadata
      let issuesData = []
      let totalCountValue = 0
      let paginationData = null
      
      if (response.data && typeof response.data === 'object') {
        // New format: { data: [...], pagination: { total: N, page: X, per_page: Y } }
        if (response.data.data && Array.isArray(response.data.data)) {
          issuesData = response.data.data
          paginationData = response.data.pagination
          // Get total count from pagination metadata - be explicit about checking for total
          if (response.data.pagination && typeof response.data.pagination.total === 'number') {
            totalCountValue = response.data.pagination.total
          } else if (typeof response.data.total === 'number') {
            totalCountValue = response.data.total
          } else {
            totalCountValue = response.data.data.length
          }
        }
        // Legacy format: { items: [...], pagination: { total: N, skip: X, limit: Y } }
        else if (response.data.items && Array.isArray(response.data.items)) {
          issuesData = response.data.items
          paginationData = response.data.pagination
          // Get total count from pagination metadata - be explicit about checking for total
          if (response.data.pagination && typeof response.data.pagination.total === 'number') {
            totalCountValue = response.data.pagination.total
          } else if (typeof response.data.total === 'number') {
            totalCountValue = response.data.total
          } else {
            totalCountValue = response.data.items.length
          }
        } 
        // Legacy format: { items: [...], total: N }
        else if (response.data.total !== undefined) {
          issuesData = Array.isArray(response.data.items) ? response.data.items : (Array.isArray(response.data) ? response.data : [])
          totalCountValue = response.data.total
        }
        // Direct array format (fallback)
        else if (Array.isArray(response.data)) {
          issuesData = response.data
          totalCountValue = response.data.length
        } else {
          // Fallback: ensure we always have an array
          issuesData = Array.isArray(response.data) ? response.data : []
          totalCountValue = Array.isArray(response.data) ? response.data.length : 0
        }
      }
      
      // Ensure issuesData is always an array
      if (!Array.isArray(issuesData)) {
        console.warn('Issues data is not an array, defaulting to empty array:', issuesData)
        issuesData = []
      }
      
      
      setIssues(issuesData)
      setTotalCount(totalCountValue)
      setPaginationInfo(paginationData)
      
      
      // Calculate total pages based on total count
      const calculatedTotalPages = Math.ceil(totalCountValue / itemsPerPage)
      setTotalPages(calculatedTotalPages)
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch issues:', error)
      toast.error('Failed to load issues')
      setIssues([])
      setTotalCount(0)
      setTotalPages(1)
      setLoading(false)
    }
  }

  // Refresh issues
  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchIssues()
    setRefreshing(false)
    toast.success('Issues refreshed')
  }

  // Handle opening location modal
  const handleViewLocation = useCallback((issue) => {
    // Don't set selectedIssue to avoid interfering with URL-based modal system
    // Instead, we'll pass the issue directly to the modal
    setShowLocationModal(true)
    // Store the issue for the location modal separately
    setLocationModalIssue(issue)
  }, [])

  // Removed handleLocationChange - IsolatedLocationMap manages its own position

  // Handle address update from reverse geocoding
  const handleAddressUpdate = (addressData, newLatitude, newLongitude) => {
    if (selectedIssue) {
      // Calculate new Plus Code from updated coordinates
      const newPlusCode = calculatePlusCode(newLatitude, newLongitude)
      
      // Use functional update to ensure React detects the change
      setSelectedIssue(prev => ({
        ...prev,
        house_number: addressData.house_number,
        road: addressData.road,
        address2: addressData.address2,
        city: addressData.city,
        state: addressData.state,
        postcode: addressData.postcode,
        county: addressData.county,
        country_iso2: addressData.country_iso2,
        latitude: newLatitude,
        longitude: newLongitude,
        plus_code: newPlusCode
      }))
      setAddressUpdated(true) // Mark that address was updated
      
      // Show success toast with Plus Code info
      const toastMessage = newPlusCode 
        ? `Address and location updated from new pin!\nPlus Code: ${newPlusCode}`
        : 'Address and location updated from new pin!'
      toast.success(toastMessage, { duration: 4000 })
    }
  }

  // Add new issue
  const handleAddIssue = async () => {
    try {
      const issueData = {
        title: newIssue.title,
        description: newIssue.description || null,
        category: newIssue.category,
        priority: newIssue.priority,
        status: newIssue.status,
        house_number: newIssue.house_number || null,
        road: newIssue.road || null,
        address2: newIssue.address2 || null,
        city: newIssue.city || null,
        state: newIssue.state || null,
        postcode: newIssue.postcode || null,
        county: newIssue.county || null,
        country_iso2: newIssue.country_iso2,
        facility_name: newIssue.facility_name || null,
        facility_type: newIssue.facility_type || null,
        facility_subtype: newIssue.facility_subtype || null
      }

      const response = await axios.post(`${API_BASE}/issues`, issueData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setIssues([response.data, ...issues])
      setShowAddIssue(false)
      setNewIssue({
        title: '',
        description: '',
        category: categories.length > 0 ? categories[0].name : '',
        priority: 'medium',
        status: 'open',
        house_number: '',
        road: '',
        address2: '',
        city: '',
        state: '',
        postcode: '',
        county: '',
        country_iso2: 'US',
        facility_name: '',
        facility_type: '',
        facility_subtype: ''
      })
      toast.success('Issue created successfully')
    } catch (error) {
      console.error('Failed to create issue:', error)
      toast.error('Failed to create issue')
    }
  }

  // Update issue
  const handleUpdateIssue = async () => {
    try {
      const issueData = {
        title: selectedIssue.title,
        description: selectedIssue.description || null,
        category: selectedIssue.category,
        priority: selectedIssue.priority,
        status: selectedIssue.status,
        house_number: selectedIssue.house_number || null,
        road: selectedIssue.road || null,
        address2: selectedIssue.address2 || null,
        city: selectedIssue.city || null,
        state: selectedIssue.state || null,
        postcode: selectedIssue.postcode || null,
        county: selectedIssue.county || null,
        country_iso2: selectedIssue.country_iso2 || 'US',
        latitude: selectedIssue.latitude || null,
        longitude: selectedIssue.longitude || null,
        facility_name: selectedIssue.facility_name || null,
        facility_type: selectedIssue.facility_type || null,
        facility_subtype: selectedIssue.facility_subtype || null
      }

      await axios.put(`${API_BASE}/issues/${selectedIssue.id}`, issueData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setIssues(issues.map(issue =>
        issue.id === selectedIssue.id ? selectedIssue : issue
      ))
      closeIssueModal() // This will clear URL parameters and close modal
      toast.success('Issue updated successfully')
    } catch (error) {
      console.error('Failed to update issue:', error)
      toast.error('Failed to update issue')
    }
  }

  // Delete issue
  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Are you sure you want to delete this issue?')) return

    try {
      await axios.delete(`${API_BASE}/issues/${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setIssues(issues.filter(issue => issue.id !== issueId))
      toast.success('Issue deleted successfully')
    } catch (error) {
      console.error('Failed to delete issue:', error)
      toast.error('Failed to delete issue')
    }
  }

  // Toggle individual issue selection
  const toggleIssueSelection = (issueId) => {
    setSelectedIssueIds(prev => {
      if (prev.includes(issueId)) {
        return prev.filter(id => id !== issueId)
      } else {
        return [...prev, issueId]
      }
    })
  }

  // Toggle all issues selection
  const toggleAllIssuesSelection = () => {
    if (selectedIssueIds.length === paginatedIssues.length) {
      setSelectedIssueIds([])
    } else {
      setSelectedIssueIds(paginatedIssues.map(issue => issue.id))
    }
  }

  // Bulk delete selected issues
  const handleBulkDelete = async () => {
    if (selectedIssueIds.length === 0) return
    
    const count = selectedIssueIds.length
    if (!window.confirm(`Are you sure you want to delete ${count} issue${count > 1 ? 's' : ''}?`)) return

    setIsDeleting(true)
    let successCount = 0
    let failCount = 0

    for (const issueId of selectedIssueIds) {
      try {
        await axios.delete(`${API_BASE}/issues/${issueId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        successCount++
      } catch (error) {
        console.error(`Failed to delete issue ${issueId}:`, error)
        failCount++
      }
    }

    // Refresh the issues list
    await fetchIssues()
    
    // Clear selection
    setSelectedIssueIds([])
    setIsDeleting(false)

    // Show results
    if (successCount > 0 && failCount === 0) {
      toast.success(`Successfully deleted ${successCount} issue${successCount > 1 ? 's' : ''}`)
    } else if (successCount > 0 && failCount > 0) {
      toast.success(`Deleted ${successCount} issue${successCount > 1 ? 's' : ''}, ${failCount} failed`)
    } else {
      toast.error('Failed to delete selected issues')
    }
  }

  // Change issue status
  const handleStatusChange = async (issueId, newStatus) => {
    try {
      const updatedIssue = {
        ...issues.find(issue => issue.id === issueId),
        status: newStatus,
        resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null
      }

      await axios.put(`${API_BASE}/issues/${issueId}`, updatedIssue, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setIssues(issues.map(issue =>
        issue.id === issueId ? updatedIssue : issue
      ))

      const statusText = newStatus.replace('_', ' ')
      toast.success(`Issue status changed to ${statusText}`)
    } catch (error) {
      console.error('Failed to update status:', error)
      toast.error('Failed to update status')
    }
  }

  // Modal management functions with URL updates
  const openIssueModal = (issue, mode = 'preview') => {
    // Store original address for comparison
    const issueWithOriginal = {
      ...issue,
      original_full_address: issue.full_address
    }
    setSelectedIssue(issueWithOriginal)
    setModalMode(mode)
    setAddressUpdated(false) // Reset address update flag
    setOriginalLocation({ lat: issue.latitude, lng: issue.longitude }) // Store original location
    setSearchParams({ issue: issue.id, mode: mode })
  }

  const closeIssueModal = () => {
    setSelectedIssue(null)
    setModalMode('preview')
    setSearchParams({}) // Clear URL parameters
  }

  const switchModalMode = (newMode) => {
    if (selectedIssue) {
      setModalMode(newMode)
      setSearchParams({ issue: selectedIssue.id, mode: newMode })
    }
  }

  // Client-side search function
  const filterIssuesBySearch = (issuesList, searchQuery) => {
    if (!searchQuery.trim()) {
      return issuesList
    }
    
    const query = searchQuery.toLowerCase().trim()
    return issuesList.filter(issue => {
      // Search in title, description, location, reporter email, and image info
      const searchableText = [
        issue.title || '',
        issue.description || '',
        issue.location || '',
        issue.reporter_email || '',
        issue.address || '',
        issue.city || '',
        issue.state || '',
        issue.zip_code || '',
        // Include attachment info if available
        ...(issue.attachment_summaries || []).map(att => att.filename || '')
      ].join(' ').toLowerCase()
      
      return searchableText.includes(query)
    })
  }

  // Changing a filter or the search term always returns to the first page.
  // This lives in the event handlers rather than an effect on purpose: an
  // effect keyed on `issues` also fires after every fetch, so it reset the page
  // on each Next click and bounced the user straight back to page 1. Updating
  // the filter and the page together also keeps it to a single refetch.
  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value)
    setCurrentPage(1)
  }

  // Derive the visible rows from the fetched page and the search term.
  useEffect(() => {
    setFilteredIssues(filterIssuesBySearch(issues, searchTerm))
  }, [issues, searchTerm])

  // URL parameter synchronization
  useEffect(() => {
    const issueId = searchParams.get('issue')
    const mode = searchParams.get('mode') || 'preview'
    
    if (issueId) {
      // Find issue in current data
      const issue = issues.find(i => i.id === parseInt(issueId))
      if (issue) {
        // Only update selectedIssue if it's not already set or if it's a different issue
        setSelectedIssue(prev => {
          if (!prev || prev.id !== issue.id) {
            return issue
          }
          return prev // Keep the current selectedIssue (which may have been modified)
        })
        setModalMode(mode)
      } else if (issues.length > 0) {
        // Issue not found in current data, clear URL parameters
        setSearchParams({})
        setSelectedIssue(null)
        setModalMode('preview')
      }
    } else {
      // No issue in URL, clear modal if open
      if (selectedIssue) {
        setSelectedIssue(null)
        setModalMode('preview')
      }
    }
  }, [searchParams, issues, selectedIssue, setSearchParams])
  
  // Effect for other filters and pagination (immediate)
  useEffect(() => {
    if (token) {
      fetchCategories()
      fetchIssues()
    }
    // The listed deps are exactly the intended refetch triggers. fetchIssues /
    // fetchCategories are recreated every render, so listing them would fetch
    // in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentPage, filterStatus, filterPriority, filterCategory])

  // Pagination is server-side: fetchIssues() requests one page via skip/limit,
  // so `issues` already IS the current page. Do NOT slice it again by
  // currentPage — that returned an empty array for every page past the first.
  // `filteredIssues` only narrows the fetched page further by the search term.
  const paginatedIssues = Array.isArray(filteredIssues) ? filteredIssues : []
  const filteredTotalCount = Array.isArray(filteredIssues) ? filteredIssues.length : 0
  const filteredTotalPages = Math.ceil(filteredTotalCount / itemsPerPage)
  
  // Use appropriate pagination based on whether we're searching or not
  const displayTotalPages = searchTerm.trim() ? filteredTotalPages : totalPages
  const displayTotalCount = searchTerm.trim() ? filteredTotalCount : totalCount
  

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'in_progress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'resolved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <HiClock className="h-4 w-4" />
      case 'in_progress': return <HiExclamation className="h-4 w-4" />
      case 'resolved': return <HiCheckCircle className="h-4 w-4" />
      case 'closed': return <HiX className="h-4 w-4" />
      default: return <HiClock className="h-4 w-4" />
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A'
    
    const now = new Date()
    const created = new Date(dateString)
    const diffMs = now - created
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) // Use Math.ceil to round up for days
    
    // Less than 1 hour
    if (diffMins < 60) {
      return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`
    }
    
    // Less than 3 hours
    if (diffHours < 3) {
      const remainingMins = diffMins % 60
      return `${diffHours}h ${remainingMins}m ago`
    }
    
    // Less than 14 hours
    if (diffHours < 14) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
    }
    
    // Less than 7 days
    if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
    }
    
    // More than 7 days - show full timestamp in two rows
    const date = created.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    const time = created.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    
    return (
      <div className="text-center">
        <div className="text-xs">{date}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{time}</div>
      </div>
    )
  }

  // Image carousel functions
  // NOTE: built but not yet wired to a thumbnail click handler in the table.
  // eslint-disable-next-line no-unused-vars
  const handleThumbnailClick = (issue) => {
    // Build images array from attachments
    const images = []
    
    // Add thumbnail as first image if it exists
    if (issue.thumbnail_url) {
      images.push({
        url: issue.thumbnail_url,
        alt: `Thumbnail: ${issue.title}`,
        type: 'thumbnail'
      })
    }
    
    // Add other attachments if they exist
    if (issue.attachment_summaries && Array.isArray(issue.attachment_summaries)) {
      issue.attachment_summaries.forEach((attachment, index) => {
        // Skip the thumbnail (already added above) and only add image attachments
        if (!attachment.thumbnail && attachment.url) {
          images.push({
            url: attachment.url,
            alt: `Attachment ${index + 1}: ${issue.title}`,
            type: 'attachment',
            filename: `attachment_${index + 1}`
          })
        }
      })
    }
    
    if (images.length > 0) {
      setCarouselImages(images)
      setCarouselTitle(issue.title)
      setCarouselInitialIndex(0) // Always start with thumbnail
      setShowImageCarousel(true)
    }
  }

  const closeImageCarousel = () => {
    setShowImageCarousel(false)
    setCarouselImages([])
    setCarouselTitle('')
    setCarouselInitialIndex(0)
  }

  const getLocationDisplay = (issue) => {
    if (issue.full_address) return issue.full_address
    if (issue.city && issue.state) return `${issue.city}, ${issue.state}`
    if (issue.city) return issue.city
    return 'Location not specified'
  }

  // New structured address display component
  const AddressDisplay = ({ issue }) => {
    // Check if we have structured address data
    const hasStructuredData = issue.house_number || issue.road || issue.city || issue.state || issue.postcode || issue.country_iso2
    
    if (!hasStructuredData) {
      // Fallback to full_address or simple display
      return (
        <div className="text-sm text-gray-900 dark:text-white">
          <div className="flex items-start space-x-2">
            <PlusCode plusCode={issue.plus_code} />
            <div className="flex-1">
              {getLocationDisplay(issue)}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="text-sm text-gray-900 dark:text-white min-w-0">
        <div className="flex items-start space-x-2">
          <PlusCode plusCode={issue.plus_code} />
          <div className="flex-1">
            {/* Row 1: house_number + road */}
            {(issue.house_number || issue.road) && (
              <div className="flex items-start space-x-1 mb-1 text-xs text-gray-600 dark:text-gray-400">
                {issue.house_number && (
                  <span className="font-medium flex-shrink-0">{issue.house_number}</span>
                )}
                {issue.road && (
                  <span className="break-words">{issue.road}</span>
                )}
              </div>
            )}
            
            {/* Row 2: city, state, postcode, country */}
            {(issue.city || issue.state || issue.postcode || issue.country_iso2) && (
              <div className="flex items-start space-x-1 text-xs text-gray-600 dark:text-gray-400">
                {issue.city && <span className="break-words">{issue.city}</span>}
                {issue.city && issue.state && <span className="flex-shrink-0">,</span>}
                {issue.state && <span className="break-words">{issue.state}</span>}
                {issue.postcode && <span className="flex-shrink-0">{issue.postcode}</span>}
                {issue.country_iso2 && issue.country_iso2 !== 'US' && (
                  <span className="ml-1 text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded flex-shrink-0">
                    {issue.country_iso2}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Issue Administration
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Manage and track city infrastructure issues and maintenance requests.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {displayTotalCount}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {searchTerm.trim() ? 'Filtered Issues' : 'Total Issues'}
              </div>
            </div>
            <button
              onClick={() => setShowAddIssue(true)}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center"
            >
              <HiPlus className="h-4 w-4 mr-2" />
              Add Issue
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Issues
            </label>
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, description, location, reporter, or image..."
                value={searchTerm}
                onChange={handleFilterChange(setSearchTerm)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={handleFilterChange(setFilterStatus)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Priority
            </label>
            <select
              value={filterPriority}
              onChange={handleFilterChange(setFilterPriority)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={handleFilterChange(setFilterCategory)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end space-x-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors duration-200 flex items-center"
            >
              <HiRefresh className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors duration-200">
              <HiFilter className="inline h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Issues Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Issues ({searchTerm.trim() ? `${displayTotalCount} of ${totalCount}` : displayTotalCount} total)
          </h3>
          {selectedIssueIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className={`text-sm font-bold transition-colors ${
                isDeleting
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300'
              }`}
            >
              {isDeleting ? (
                <>
                  <Spinner size="sm" className="inline mr-2" />
                  Deleting...
                </>
              ) : (
                `Delete (${selectedIssueIds.length})`
              )}
            </button>
          )}
        </div>

        {/* Pagination - Top */}
        {displayTotalPages > 1 && (
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, displayTotalCount)} of {displayTotalCount} results
                {searchTerm.trim() && ` (filtered from ${totalCount} total)`}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {displayTotalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(displayTotalPages, currentPage + 1))}
                  disabled={currentPage === displayTotalPages}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16">
                  <input
                    type="checkbox"
                    checked={selectedIssueIds.length > 0 && selectedIssueIds.length === paginatedIssues.length}
                    onChange={toggleAllIssuesSelection}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </th>
                <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-40">
                  Image
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Issue
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-auto">
                  Location
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24">
                  Created
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {Array.isArray(paginatedIssues) && paginatedIssues.length > 0 ? (
                paginatedIssues.map((issue) => (
                  <tr key={issue.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 h-20">
                    <td className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedIssueIds.includes(issue.id)}
                        onChange={() => toggleIssueSelection(issue.id)}
                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>
        <td className="px-0 py-1 w-40 overflow-visible">
          <div className="flex items-center justify-center w-full h-full overflow-visible">
                        {issue.thumbnail_url || (issue.attachment_summaries && issue.attachment_summaries.length > 0) ? (
                          <ThumbnailFan
                            images={[
                              // Add thumbnail as first image
                              ...(issue.thumbnail_url ? [{
                                url: issue.thumbnail_url,
                                alt: `Thumbnail: ${issue.title}`,
                                type: 'thumbnail'
                              }] : []),
                              // Add other image attachments (skip thumbnail duplicates)
                              ...(issue.attachment_summaries ? issue.attachment_summaries
                                .filter(attachment => !attachment.thumbnail && attachment.url)
                                .map((attachment, index) => ({
                                  url: attachment.url,
                                  alt: `Attachment ${index + 1}: ${issue.title}`,
                                  type: 'attachment',
                                  filename: `attachment_${index + 1}`
                                })) : [])
                            ]}
                            maxThumbnails={4}
                            size="small"
                            onImageClick={(image, index) => {
                              // Build full images array for carousel
                              const allImages = [
                                ...(issue.thumbnail_url ? [{
                                  url: issue.thumbnail_url,
                                  alt: `Thumbnail: ${issue.title}`,
                                  type: 'thumbnail'
                                }] : []),
                                ...(issue.attachment_summaries ? issue.attachment_summaries
                                  .filter(attachment => !attachment.thumbnail && attachment.url)
                                  .map((attachment, index) => ({
                                    url: attachment.url,
                                    alt: `Attachment ${index + 1}: ${issue.title}`,
                                    type: 'attachment',
                                    filename: `attachment_${index + 1}`
                                  })) : [])
                              ]
                              
                              setCarouselImages(allImages)
                              setCarouselTitle(issue.title)
                              setCarouselInitialIndex(index)
                              setShowImageCarousel(true)
                            }}
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg border border-white flex items-center justify-center text-gray-400 dark:text-gray-500 mx-auto">
                            <HiLocationMarker className="h-6 w-6" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {issue.title}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {issue.description ? issue.description.substring(0, 60) + '...' : 'No description'}
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Reported {formatDate(issue.created_at)} by {issue.reporter_name || 'Unknown'}
                        </div>
                        {issue.city && issue.state && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {issue.city}, {issue.state}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {issue.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge color={issue.priority === 'critical' ? 'failure' : issue.priority === 'high' ? 'warning' : issue.priority === 'medium' ? 'warning' : 'success'}>
                        {issue.priority}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge color={issue.status === 'open' ? 'warning' : issue.status === 'in_progress' ? 'info' : issue.status === 'resolved' ? 'success' : 'gray'}>
                        <div className="flex items-center">
                          {getStatusIcon(issue.status)}
                          <span className="ml-1">{issue.status.replace('_', ' ')}</span>
                        </div>
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-start text-sm text-gray-900 dark:text-white">
                        <div className="flex-1 min-w-0">
                          <AddressDisplay issue={issue} />
                        </div>
                        {issue.latitude && issue.longitude && (
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleViewLocation(issue)
                            }}
                            className="ml-2 p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                            title="View on map"
                          >
                            <HiZoomIn className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td 
                      className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                      title={formatDate(issue.created_at)}
                    >
                      <div className="text-center">
                        {typeof formatRelativeTime(issue.created_at) === 'string' ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatRelativeTime(issue.created_at)}
                          </div>
                        ) : (
                          formatRelativeTime(issue.created_at)
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col items-center space-y-0.5">
                        <button
                          onClick={() => openIssueModal(issue, 'preview')}
                          title="View Details"
                          className="w-12 h-6 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1 dark:focus:ring-offset-gray-800 flex items-center justify-center"
                        >
                          <HiEye className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => openIssueModal(issue, 'edit')}
                          title="Edit Issue"
                          className="w-12 h-6 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1 dark:focus:ring-offset-gray-800 flex items-center justify-center"
                        >
                          <HiPencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteIssue(issue.id)}
                          title="Delete Issue"
                          className="w-12 h-6 bg-gray-500 hover:bg-gray-600 text-red-400 text-xs font-medium rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 flex items-center justify-center"
                        >
                          <HiTrash className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {loading ? 'Loading issues...' : 'No issues found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - Bottom */}
        {displayTotalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, displayTotalCount)} of {displayTotalCount} results
                {searchTerm.trim() && ` (filtered from ${totalCount} total)`}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {displayTotalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(displayTotalPages, currentPage + 1))}
                  disabled={currentPage === displayTotalPages}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Issue Modal */}
      {showAddIssue && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Add New Issue
                </h3>
                <button
                  onClick={() => setShowAddIssue(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newIssue.title}
                    onChange={(e) => setNewIssue({...newIssue, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Brief description of the issue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newIssue.description}
                    onChange={(e) => setNewIssue({...newIssue, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Detailed description of the issue (optional)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category *
                    </label>
                    <select
                      value={newIssue.category}
                      onChange={(e) => setNewIssue({...newIssue, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Priority
                    </label>
                    <select
                      value={newIssue.priority}
                      onChange={(e) => setNewIssue({...newIssue, priority: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>


                <div className="grid grid-cols-6 gap-2">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      House #
                    </label>
                    <input
                      type="text"
                      value={newIssue.house_number}
                      onChange={(e) => setNewIssue({...newIssue, house_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="123456"
                      maxLength="6"
                    />
                  </div>

                  <div className="col-span-5">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Street
                    </label>
                    <input
                      type="text"
                      value={newIssue.road}
                      onChange={(e) => setNewIssue({...newIssue, road: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Street Name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={newIssue.address2}
                      onChange={(e) => setNewIssue({...newIssue, address2: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Apartment, suite, unit, building, floor, etc."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-7">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={newIssue.city}
                      onChange={(e) => setNewIssue({...newIssue, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="City Name"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={newIssue.state}
                      onChange={(e) => setNewIssue({...newIssue, state: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center"
                      placeholder="TX"
                      maxLength="2"
                    />
                  </div>

                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ZIP
                    </label>
                    <input
                      type="text"
                      value={newIssue.postcode}
                      onChange={(e) => setNewIssue({...newIssue, postcode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="75069"
                      maxLength="5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      County
                    </label>
                    <input
                      type="text"
                      value={newIssue.county}
                      onChange={(e) => setNewIssue({...newIssue, county: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="County"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      value={newIssue.country_iso2}
                      onChange={(e) => setNewIssue({...newIssue, country_iso2: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Country Code (e.g., US)"
                    />
                  </div>
                </div>

                {/* Facility Fields */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Facility Name
                    </label>
                    <input
                      type="text"
                      value={newIssue.facility_name}
                      onChange={(e) => setNewIssue({...newIssue, facility_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Facility Name"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Facility Type
                      </label>
                      <input
                        type="text"
                        value={newIssue.facility_type}
                        onChange={(e) => setNewIssue({...newIssue, facility_type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Facility Type"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Facility Subtype
                      </label>
                      <input
                        type="text"
                        value={newIssue.facility_subtype}
                        onChange={(e) => setNewIssue({...newIssue, facility_subtype: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Facility Subtype"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddIssue(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddIssue}
                  disabled={!newIssue.title || !newIssue.category}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  Create Issue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue Details Modal */}
      {selectedIssue && (
        <div key={`modal-${selectedIssue.id}-${selectedIssue.full_address}`} className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={closeIssueModal}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Close modal"
                  >
                    <HiX className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Issue Details {modalMode === 'edit' ? '(Edit Mode)' : '(Preview Mode)'}
                    </h3>
                    {modalMode === 'preview' && (
                      <button
                        onClick={() => switchModalMode('edit')}
                        className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded transition-colors duration-200 flex items-center"
                      >
                        <HiPencil className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    )}
                    {modalMode === 'edit' && (
                      <button
                        onClick={() => switchModalMode('preview')}
                        className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded transition-colors duration-200 flex items-center"
                      >
                        <HiEye className="h-4 w-4 mr-1" />
                        Preview
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {modalMode === 'edit' && (
                    <button
                      onClick={handleUpdateIssue}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200"
                    >
                      Save Changes
                    </button>
                  )}
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Issue ID: <span className="font-mono font-medium">{selectedIssue.id}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Status:</span>
                    <select
                      value={selectedIssue.status}
                      onChange={(e) => handleStatusChange(selectedIssue.id, e.target.value)}
                      disabled={modalMode === 'preview'}
                      className={`px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white ${
                        modalMode === 'preview' 
                          ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-700'
                      }`}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={selectedIssue.title}
                      onChange={(e) => setSelectedIssue({...selectedIssue, title: e.target.value})}
                      readOnly={modalMode === 'preview'}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white ${
                        modalMode === 'preview' 
                          ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-700'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={selectedIssue.description || ''}
                      onChange={(e) => {
                        setSelectedIssue({...selectedIssue, description: e.target.value})
                        // Auto-resize textarea
                        e.target.style.height = 'auto'
                        e.target.style.height = e.target.scrollHeight + 'px'
                      }}
                      readOnly={modalMode === 'preview'}
                      rows={3}
                      style={{ minHeight: '80px', resize: 'vertical' }}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white overflow-hidden ${
                        modalMode === 'preview' 
                          ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' 
                          : 'bg-white dark:bg-gray-700'
                      }`}
                    />
                  </div>

                  {/* Two columns under description */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Left column - Thumbnail Fan */}
                    <div>
                      {(selectedIssue.thumbnail_url || (selectedIssue.attachment_summaries && selectedIssue.attachment_summaries.length > 0)) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Images
                          </label>
                          <div className="flex justify-center">
                            <ThumbnailFan
                              images={[
                                ...(selectedIssue.thumbnail_url ? [{
                                  url: selectedIssue.thumbnail_url,
                                  alt: `Thumbnail: ${selectedIssue.title}`,
                                  type: 'thumbnail'
                                }] : []),
                                ...(selectedIssue.attachment_summaries ? selectedIssue.attachment_summaries
                                  .filter(attachment => !attachment.thumbnail && attachment.url)
                                  .map((attachment, index) => ({
                                    url: attachment.url,
                                    alt: `Attachment ${index + 1}: ${selectedIssue.title}`,
                                    type: 'attachment',
                                    filename: `attachment_${index + 1}`
                                  })) : [])
                              ]}
                              maxThumbnails={4}
                              size="small"
                              onImageClick={(image, index) => {
                                const allImages = [
                                  ...(selectedIssue.thumbnail_url ? [{
                                    url: selectedIssue.thumbnail_url,
                                    alt: `Thumbnail: ${selectedIssue.title}`,
                                    type: 'thumbnail'
                                  }] : []),
                                  ...(selectedIssue.attachment_summaries ? selectedIssue.attachment_summaries
                                    .filter(attachment => !attachment.thumbnail && attachment.url)
                                    .map((attachment, index) => ({
                                      url: attachment.url,
                                      alt: `Attachment ${index + 1}: ${selectedIssue.title}`,
                                      type: 'attachment',
                                      filename: `attachment_${index + 1}`
                                    })) : [])
                                ]
                                setCarouselImages(allImages)
                                setCarouselTitle(selectedIssue.title)
                                setCarouselInitialIndex(index)
                                setShowImageCarousel(true)
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right column - Category and Priority */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Category
                        </label>
                        <select
                          value={selectedIssue.category}
                          onChange={(e) => setSelectedIssue({...selectedIssue, category: e.target.value})}
                          disabled={modalMode === 'preview'}
                          className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white ${
                            modalMode === 'preview' 
                              ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' 
                              : 'bg-white dark:bg-gray-700'
                          }`}
                        >
                          {categories.map(category => (
                            <option key={category.id} value={category.name}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Priority
                        </label>
                        <select
                          value={selectedIssue.priority}
                          onChange={(e) => setSelectedIssue({...selectedIssue, priority: e.target.value})}
                          disabled={modalMode === 'preview'}
                          className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white ${
                            modalMode === 'preview' 
                              ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' 
                              : 'bg-white dark:bg-gray-700'
                          }`}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Address Fields */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Full Address
                        {addressUpdated && (
                          <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                            ✓ Updated from map
                          </span>
                        )}
                      </label>
                      <PlusCode plusCode={selectedIssue.plus_code} />
                    </div>
                    <div className="w-full px-3 py-2 border border-gray-200 dark:border-gray-500 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm">
                      {computeFullAddress(selectedIssue) || 'No address specified'}
                    </div>
                  </div>


                  {/* Individual address fields - only show in edit mode */}
                  {modalMode === 'edit' && (
                    <>
                      <div className="grid grid-cols-6 gap-2">
                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            House #
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.house_number || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, house_number: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            maxLength="6"
                          />
                        </div>

                        <div className="col-span-5">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Street
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.road || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, road: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Address Line 2
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.address2 || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, address2: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            placeholder="Apartment, suite, unit, building, floor, etc."
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-7">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            City
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.city || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, city: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                          />
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            State
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.state || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, state: e.target.value.toUpperCase()})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700 text-center"
                            maxLength="2"
                          />
                        </div>

                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            ZIP
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.postcode || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, postcode: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            maxLength="5"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            County
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.county || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, county: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Country
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.country_iso2 || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, country_iso2: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Facility Fields - only show in edit mode */}
                  {modalMode === 'edit' && (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Facility Name
                        </label>
                        <input
                          type="text"
                          value={selectedIssue.facility_name || ''}
                          onChange={(e) => setSelectedIssue({...selectedIssue, facility_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Facility Type
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.facility_type || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, facility_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Facility Subtype
                          </label>
                          <input
                            type="text"
                            value={selectedIssue.facility_subtype || ''}
                            onChange={(e) => setSelectedIssue({...selectedIssue, facility_subtype: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Facility Fields - show in preview mode with expandable details */}
                  {modalMode === 'preview' && (selectedIssue.facility_name || selectedIssue.facility_type || selectedIssue.facility_subtype) && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Facility:
                        </label>
                        <span className="text-sm text-gray-900 dark:text-white">
                          {selectedIssue.facility_name || 'No facility name'}
                        </span>
                      </div>
                      
                      {(selectedIssue.facility_type || selectedIssue.facility_subtype) && (
                        <div className="ml-4 space-y-1">
                          {selectedIssue.facility_type && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Type: {selectedIssue.facility_type}
                            </div>
                          )}
                          {selectedIssue.facility_subtype && (
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Subtype: {selectedIssue.facility_subtype}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Map */}
                  {(selectedIssue.latitude && selectedIssue.longitude) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Location Map
                      </label>
                      <div className={`w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 ${
                        modalMode === 'edit' ? 'h-72' : 'h-56'
                      }`}>
                        <IsolatedLocationMap
                          key={`map-${selectedIssue.id}-isolated`}
                          initialLatitude={selectedIssue.latitude}
                          initialLongitude={selectedIssue.longitude}
                          onAddressUpdate={handleAddressUpdate}
                          editable={modalMode === 'edit'}
                          height={modalMode === 'edit' ? "280px" : "208px"}
                          showDirectionalCone={!!extractDirectionFromAttachments(selectedIssue.attachment_summaries)}
                          coneDirection={extractDirectionFromAttachments(selectedIssue.attachment_summaries) || 0}
                          coneLength={100}
                          coneWidth={30}
                          coneOpacity={0.6}
                        />
                      </div>
                    </div>
                  )}

                  {/* Metadata Fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reporter ID
                    </label>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedIssue.reporter_id}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Created At
                    </label>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(selectedIssue.created_at)}
                    </div>
                  </div>

                  {selectedIssue.resolved_at && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Resolved At
                      </label>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(selectedIssue.resolved_at)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Image Carousel */}
      <ImageCarousel
        images={carouselImages}
        isOpen={showImageCarousel}
        onClose={closeImageCarousel}
        initialIndex={carouselInitialIndex}
        title={carouselTitle}
      />

      {/* Location Modal */}
      {showLocationModal && locationModalIssue && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998]">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Issue Location - {locationModalIssue.title}
                </h3>
                <button
                  onClick={() => {
                    setShowLocationModal(false)
                    setLocationModalIssue(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <HiX className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Issue Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Issue ID
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">{locationModalIssue.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <Badge color={getStatusColor(locationModalIssue.status)}>
                      {locationModalIssue.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">{locationModalIssue.description || 'No description'}</p>
                  </div>
                </div>

                {/* Map */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Location
                  </label>
                  <IsolatedLocationMap
                    initialLatitude={locationModalIssue.latitude}
                    initialLongitude={locationModalIssue.longitude}
                    onAddressUpdate={handleAddressUpdate}
                    editable={true}
                    height="h-96"
                    originalLocation={{
                      lat: parseFloat(locationModalIssue.latitude),
                      lng: parseFloat(locationModalIssue.longitude)
                    }}
                    showDirectionalCone={!!extractDirectionFromAttachments(locationModalIssue.attachment_summaries)}
                    coneDirection={extractDirectionFromAttachments(locationModalIssue.attachment_summaries) || 0}
                    coneLength={100}
                    coneWidth={30}
                    coneOpacity={0.6}
                  />
                </div>

                {/* Address Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Street Address
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {locationModalIssue.house_number && locationModalIssue.road 
                        ? `${locationModalIssue.house_number} ${locationModalIssue.road}`
                        : 'Not specified'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      City, State, ZIP
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {[locationModalIssue.city, locationModalIssue.state, locationModalIssue.postcode]
                        .filter(Boolean)
                        .join(', ') || 'Not specified'
                      }
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Full Address
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {computeFullAddress(locationModalIssue) || 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowLocationModal(false)
                    setLocationModalIssue(null)
                  }}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IssueAdmin
